import {
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  type Firestore,
  type FirestoreError,
  type Unsubscribe,
} from 'firebase/firestore'
import { getDiagnosticsAdapterMode, type DiagnosticsAdapterMode } from '../../config/diagnosticsConfig'
import { firestoreDb } from '../firebase'
import type {
  AiExecutionTrace,
  DiagnosticEvent,
  DiagnosticFailureClass,
  DiagnosticMetricSnapshot,
  DiagnosticSubsystem,
  OperationalHealthState,
  RetrievalTrace,
  WorkspaceOperationalHealthSummary,
} from '../../types/diagnostics'

const SUBSYSTEMS: DiagnosticSubsystem[] = ['ai', 'retrieval', 'integrations', 'uploads', 'mobile_sync', 'automations', 'governance']
const MAX_RECENT_EVENTS = 20
const MAX_RECENT_TRACES = 12

const emptyMetricWindow = (): DiagnosticMetricSnapshot => ({
  count: 0,
  degradedCount: 0,
  failureCount: 0,
  retryCount: 0,
  totalLatencyMs: 0,
  totalCompressionRatio: 0,
  lastEventAtIso: null,
})

const fallbackState = (workspaceId: string, subsystem: DiagnosticSubsystem): OperationalHealthState => ({
  workspaceId,
  subsystem,
  score: 100,
  status: 'healthy',
  generatedAtIso: new Date().toISOString(),
  summary: `${subsystem.replaceAll('_', ' ')} has no recorded diagnostics in this local window.`,
  metricWindow: emptyMetricWindow(),
  failureClasses: {},
  recentEvents: [],
})

const bounded = <T,>(items: T[], max: number) => items.slice(0, max)

export const hashDiagnosticIdentifier = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return `h_${(hash >>> 0).toString(16).padStart(8, '0')}`
}

export const classifyHealthScore = (score: number): OperationalHealthState['status'] => {
  if (score < 45) return 'critical'
  if (score < 70) return 'degraded'
  if (score < 88) return 'watch'
  return 'healthy'
}

const scoreFromMetrics = (metrics: DiagnosticMetricSnapshot) => {
  if (metrics.count <= 0) return 100
  const failurePenalty = (metrics.failureCount / metrics.count) * 45
  const degradedPenalty = (metrics.degradedCount / metrics.count) * 25
  const retryPenalty = Math.min(metrics.retryCount / Math.max(metrics.count, 1), 2) * 8
  const latencyPenalty = Math.min(metrics.totalLatencyMs / metrics.count / 1000, 12)
  return Math.max(0, Math.round(100 - failurePenalty - degradedPenalty - retryPenalty - latencyPenalty))
}

const summarizeState = (subsystem: DiagnosticSubsystem, metrics: DiagnosticMetricSnapshot, score: number) => {
  const label = subsystem.replaceAll('_', ' ')
  if (metrics.count === 0) return `${label} has no recorded diagnostics in this local window.`
  if (score < 70) return `${label} is degraded across ${metrics.count} bounded diagnostic samples.`
  if (metrics.degradedCount > 0 || metrics.retryCount > 0) return `${label} is operating with warnings across ${metrics.count} samples.`
  return `${label} is healthy across ${metrics.count} bounded diagnostic samples.`
}

const applyEventToState = (
  state: OperationalHealthState,
  event: DiagnosticEvent,
): OperationalHealthState => {
  const metricWindow = {
    ...state.metricWindow,
    count: state.metricWindow.count + 1,
    degradedCount: state.metricWindow.degradedCount + (event.degraded || event.severity !== 'info' ? 1 : 0),
    failureCount: state.metricWindow.failureCount + (event.severity === 'critical' ? 1 : 0),
    retryCount: state.metricWindow.retryCount + Math.max(0, Number(event.retryCount ?? 0)),
    totalLatencyMs: state.metricWindow.totalLatencyMs + Math.max(0, Number(event.latencyMs ?? 0)),
    lastEventAtIso: event.occurredAtIso,
  }
  const failureClasses = { ...state.failureClasses }
  if (event.failureClass) failureClasses[event.failureClass] = (failureClasses[event.failureClass] ?? 0) + 1
  const score = scoreFromMetrics(metricWindow)
  return {
    ...state,
    generatedAtIso: event.occurredAtIso,
    score,
    status: classifyHealthScore(score),
    summary: summarizeState(event.subsystem, metricWindow, score),
    metricWindow,
    failureClasses,
    recentEvents: bounded([event, ...state.recentEvents], MAX_RECENT_EVENTS),
  }
}

const applyAiTraceToState = (state: OperationalHealthState, trace: AiExecutionTrace): OperationalHealthState => {
  const event: DiagnosticEvent = {
    id: trace.traceId,
    workspaceId: trace.workspaceId,
    subsystem: 'ai',
    category: trace.clarificationRequested ? 'clarification' : trace.lowConfidence ? 'low_confidence' : 'execution_trace',
    severity: trace.lowConfidence ? 'warning' : 'info',
    failureClass: trace.lowConfidence ? 'AI_FAILURE' : undefined,
    operationHash: trace.operationHash,
    actorHash: trace.actorHash,
    occurredAtIso: trace.createdAtIso,
    latencyMs: trace.latencyMs,
    retryCount: trace.retryCount,
    degraded: trace.lowConfidence || trace.malformedJsonRepair || Boolean(trace.degradationReason),
    metrics: {
      confidence: trace.confidence ?? null,
      clarificationRequested: trace.clarificationRequested === true,
      malformedJsonRepair: trace.malformedJsonRepair === true,
      executionGraphSize: trace.executionGraphSize ?? null,
      blockedCheckpointCount: trace.blockedCheckpointCount ?? null,
      rollbackAvailable: trace.rollbackAvailable === true,
      executionRetryCount: trace.executionRetryCount ?? trace.retryCount ?? 0,
    },
  }
  const next = applyEventToState(state, event)
  return { ...next, recentAiTraces: bounded([trace, ...(next.recentAiTraces ?? [])], MAX_RECENT_TRACES) }
}

const retrievalDegraded = (trace: RetrievalTrace) =>
  trace.degradationReasons.length > 0 || trace.includedRecordCount === 0 || trace.compression.ratio < 0.08

const applyRetrievalTraceToState = (state: OperationalHealthState, trace: RetrievalTrace): OperationalHealthState => {
  const degraded = retrievalDegraded(trace)
  const event: DiagnosticEvent = {
    id: trace.traceId,
    workspaceId: trace.workspaceId,
    subsystem: 'retrieval',
    category: degraded ? 'retrieval_degradation' : 'retrieval_trace',
    severity: degraded ? 'warning' : 'info',
    failureClass: degraded ? 'RETRIEVAL_FAILURE' : undefined,
    operationHash: trace.queryHash,
    occurredAtIso: trace.createdAtIso,
    latencyMs: trace.elapsedMs,
    degraded,
    metrics: {
      rankedRecordCount: trace.rankedRecordCount,
      includedRecordCount: trace.includedRecordCount,
      omittedRecordCount: trace.omittedRecordCount,
      compressionRatio: trace.compression.ratio,
      cacheHit: trace.cache.hit,
    },
  }
  const next = applyEventToState(state, event)
  const metricWindow = {
    ...next.metricWindow,
    totalCompressionRatio: next.metricWindow.totalCompressionRatio + trace.compression.ratio,
  }
  const score = scoreFromMetrics(metricWindow)
  return {
    ...next,
    score,
    status: classifyHealthScore(score),
    metricWindow,
    recentRetrievalTraces: bounded([trace, ...(next.recentRetrievalTraces ?? [])], MAX_RECENT_TRACES),
  }
}

interface DiagnosticsAdapter {
  recordEvent: (event: DiagnosticEvent) => Promise<void>
  recordAiTrace: (trace: AiExecutionTrace) => Promise<void>
  recordRetrievalTrace: (trace: RetrievalTrace) => Promise<void>
  getHealthState: (workspaceId: string, subsystem: DiagnosticSubsystem) => Promise<OperationalHealthState>
  subscribeHealthState: (
    workspaceId: string,
    subsystem: DiagnosticSubsystem,
    onChange: (state: OperationalHealthState) => void,
    onError?: (error: unknown) => void,
  ) => Unsubscribe
}

class MemoryDiagnosticsAdapter implements DiagnosticsAdapter {
  private states = new Map<string, OperationalHealthState>()

  private key(workspaceId: string, subsystem: DiagnosticSubsystem) {
    return `${workspaceId}:${subsystem}`
  }

  private current(workspaceId: string, subsystem: DiagnosticSubsystem) {
    const key = this.key(workspaceId, subsystem)
    const state = this.states.get(key) ?? fallbackState(workspaceId, subsystem)
    this.states.set(key, state)
    return state
  }

  async recordEvent(event: DiagnosticEvent) {
    this.states.set(this.key(event.workspaceId, event.subsystem), applyEventToState(this.current(event.workspaceId, event.subsystem), event))
  }

  async recordAiTrace(trace: AiExecutionTrace) {
    this.states.set(this.key(trace.workspaceId, 'ai'), applyAiTraceToState(this.current(trace.workspaceId, 'ai'), trace))
  }

  async recordRetrievalTrace(trace: RetrievalTrace) {
    this.states.set(this.key(trace.workspaceId, 'retrieval'), applyRetrievalTraceToState(this.current(trace.workspaceId, 'retrieval'), trace))
  }

  async getHealthState(workspaceId: string, subsystem: DiagnosticSubsystem) {
    return this.current(workspaceId, subsystem)
  }

  subscribeHealthState(
    workspaceId: string,
    subsystem: DiagnosticSubsystem,
    onChange: (state: OperationalHealthState) => void,
  ): Unsubscribe {
    onChange(this.current(workspaceId, subsystem))
    return () => {}
  }
}

class FirestoreDiagnosticsAdapter extends MemoryDiagnosticsAdapter {
  private readonly db: Firestore
  constructor(db: Firestore) {
    super()
    this.db = db
  }

  private ref(workspaceId: string, subsystem: DiagnosticSubsystem) {
    return doc(this.db, 'workspaces', workspaceId, 'diagnostics', subsystem)
  }

  private async update(workspaceId: string, subsystem: DiagnosticSubsystem, updater: (state: OperationalHealthState) => OperationalHealthState) {
    const ref = this.ref(workspaceId, subsystem)
    await runTransaction(this.db, async (transaction) => {
      const snapshot = await transaction.get(ref)
      const current = snapshot.exists() ? snapshot.data() as OperationalHealthState : fallbackState(workspaceId, subsystem)
      const next = updater(current)
      transaction.set(ref, { ...next, updatedAt: serverTimestamp() }, { merge: true })
    })
  }

  async recordEvent(event: DiagnosticEvent) {
    await super.recordEvent(event)
    await this.update(event.workspaceId, event.subsystem, (state) => applyEventToState(state, event))
  }

  async recordAiTrace(trace: AiExecutionTrace) {
    await super.recordAiTrace(trace)
    await this.update(trace.workspaceId, 'ai', (state) => applyAiTraceToState(state, trace))
  }

  async recordRetrievalTrace(trace: RetrievalTrace) {
    await super.recordRetrievalTrace(trace)
    await this.update(trace.workspaceId, 'retrieval', (state) => applyRetrievalTraceToState(state, trace))
  }

  async getHealthState(workspaceId: string, subsystem: DiagnosticSubsystem) {
    const snapshot = await getDoc(this.ref(workspaceId, subsystem))
    return snapshot.exists() ? snapshot.data() as OperationalHealthState : super.getHealthState(workspaceId, subsystem)
  }

  subscribeHealthState(
    workspaceId: string,
    subsystem: DiagnosticSubsystem,
    onChange: (state: OperationalHealthState) => void,
    onError?: (error: unknown) => void,
  ): Unsubscribe {
    return onSnapshot(this.ref(workspaceId, subsystem), (snapshot) => {
      onChange(snapshot.exists() ? snapshot.data() as OperationalHealthState : fallbackState(workspaceId, subsystem))
    }, onError ? (err: FirestoreError) => onError(err) : undefined)
  }
}

class DisabledDiagnosticsAdapter extends MemoryDiagnosticsAdapter {
  async recordEvent() {}
  async recordAiTrace() {}
  async recordRetrievalTrace() {}
}

let adapterMode: DiagnosticsAdapterMode | null = null
let adapter: DiagnosticsAdapter = new MemoryDiagnosticsAdapter()

export const configureOperationalDiagnostics = (mode: DiagnosticsAdapterMode = getDiagnosticsAdapterMode()) => {
  if (adapterMode === mode) return
  adapterMode = mode
  adapter = mode === 'firestore'
    ? new FirestoreDiagnosticsAdapter(firestoreDb)
    : mode === 'disabled'
      ? new DisabledDiagnosticsAdapter()
      : new MemoryDiagnosticsAdapter()
}

const safe = async (action: () => Promise<void>) => {
  try {
    await action()
  } catch {
    // Diagnostics must never block product workflows.
  }
}

export const recordDiagnosticEvent = (event: Omit<DiagnosticEvent, 'id' | 'occurredAtIso'> & { id?: string; occurredAtIso?: string }) => {
  configureOperationalDiagnostics()
  const occurredAtIso = event.occurredAtIso ?? new Date().toISOString()
  const id = event.id ?? `diag_${event.subsystem}_${occurredAtIso}_${Math.random().toString(36).slice(2, 8)}`
  return safe(() => adapter.recordEvent({ ...event, id, occurredAtIso }))
}

export const recordAiExecutionTrace = (trace: AiExecutionTrace) => {
  configureOperationalDiagnostics()
  return safe(() => adapter.recordAiTrace(trace))
}

export const recordRetrievalTrace = (trace: RetrievalTrace) => {
  configureOperationalDiagnostics()
  return safe(() => adapter.recordRetrievalTrace(trace))
}

export const subscribeOperationalHealthState = (
  workspaceId: string,
  subsystem: DiagnosticSubsystem,
  onChange: (state: OperationalHealthState) => void,
  onError?: (error: unknown) => void,
) => {
  configureOperationalDiagnostics()
  return adapter.subscribeHealthState(workspaceId, subsystem, onChange, onError)
}

export const getWorkspaceOperationalHealthSummary = async (workspaceId: string): Promise<WorkspaceOperationalHealthSummary> => {
  configureOperationalDiagnostics()
  const states = await Promise.all(SUBSYSTEMS.map(async (subsystem) => adapter.getHealthState(workspaceId, subsystem)))
  const subsystems = Object.fromEntries(states.map((state) => [state.subsystem, state])) as Record<DiagnosticSubsystem, OperationalHealthState>
  const score = Math.round(states.reduce((total, state) => total + state.score, 0) / states.length)
  const status = classifyHealthScore(score)
  return {
    workspaceId,
    generatedAtIso: new Date().toISOString(),
    score,
    status,
    subsystems,
    summary: status === 'healthy'
      ? 'Workspace operational diagnostics are healthy across bounded subsystem summaries.'
      : `Workspace operational diagnostics are ${status}; inspect subsystem warnings before scaling execution.`,
  }
}

export const seedFirestoreDiagnosticsDocument = async (workspaceId: string, subsystem: DiagnosticSubsystem) => {
  await setDoc(doc(firestoreDb, 'workspaces', workspaceId, 'diagnostics', subsystem), fallbackState(workspaceId, subsystem), { merge: true })
}

export type { DiagnosticSubsystem, DiagnosticFailureClass }
